import { cn } from '@/shared/utilities/cn';
import { SearchOutlined } from '@mui/icons-material';
import { InputAdornment, TextField } from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Props for the SearchBox component
 */
interface SearchFieldProps {
  /** Controlled value for the search input */
  value?: string;
  /** Placeholder text displayed when input is empty */
  placeholder?: string;
  /** Callback function called when search is triggered (Enter key or debounced) */
  onSearch?: (value: string) => void;
  /** Callback function called immediately when input value changes */
  onChange?: (value: string) => void;
  /** Default value for uncontrolled component */
  defaultValue?: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether to hide the component */
  hide?: boolean;
  /** Whether the input should take full width */
  fullWidth?: boolean;
  /** Delay in milliseconds before triggering onSearch (default: 500ms) */
  delay?: number;
  /** Whether to enable debounced search (default: true) */
  enableDebounce?: boolean;
  /** Size of the input field */
  size?: 'small' | 'medium';
  /** Variant of the input field */
  variant?: 'filled' | 'outlined';
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether to auto-focus the input on mount */
  autoFocus?: boolean;
  /** Callback when input gains focus */
  onFocus?: () => void;
  /** Callback when input loses focus */
  onBlur?: () => void;
}

/**
 * A reusable search input component with debounced search functionality
 *
 * Features:
 * - Debounced search with configurable delay
 * - Immediate search on Enter key press
 * - Support for both controlled and uncontrolled usage
 * - Material-UI TextField with search icon
 * - Vietnamese placeholder by default
 *
 * @example
 * ```tsx
 * // Basic usage with debounced search
 * <SearchBox onSearch={handleSearch} />
 *
 * // Controlled with custom delay
 * <SearchBox
 *   value={searchTerm}
 *   onChange={setSearchTerm}
 *   onSearch={performSearch}
 *   debounceDelay={300}
 * />
 *
 * // Disabled debounce for immediate search
 * <SearchBox
 *   onSearch={handleSearch}
 *   enableDebounce={false}
 * />
 * ```
 */

const SearchField: React.FC<SearchFieldProps> = ({
  value,
  placeholder = 'Tìm kiếm...',
  onSearch,
  defaultValue,
  className,
  hide,
  fullWidth = false,
  onChange,
  delay = 0,
  size = 'medium',
  variant = 'filled',
  disabled = false,
  autoFocus = false,
  onFocus,
  onBlur
}) => {
  const [inputValue, setInputValue] = useState(value || defaultValue || '');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      if (onChange) {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        if (delay > 0) {
          debounceTimerRef.current = setTimeout(() => {
            onChange(newValue);
          }, delay);
        } else onChange(newValue);
      }
    },
    [onChange, delay]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && onSearch) {
      // Clear any pending debounced search
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Execute search immediately on Enter
      onSearch(inputValue);
    }
  };

  return (
    <TextField
      placeholder={placeholder}
      variant={variant}
      value={inputValue || ''}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      className={cn(className, { 'mex:!hidden': hide })}
      fullWidth={fullWidth}
      size={size}
      disabled={disabled}
      autoFocus={autoFocus}
      sx={{
        '& .MuiInputAdornment-root': {
          margin: '0 !important'
        },
        '& .MuiInputBase-input.MuiFilledInput-input': {
          padding: '12px 16px',
          fontSize: '14px',
          lineHeight: '1.5'
        }
      }}
      slotProps={{
        input: {
          sx: {
            paddingTop: '4px !important'
          },
          startAdornment: (
            <InputAdornment position='start'>
              <SearchOutlined color='primary' />
            </InputAdornment>
          )
        }
      }}
    />
  );
};

export default SearchField;
